import { diff } from 'jsondiffpatch'
import { type GetStaticPropsResult, type GetStaticPathsResult } from 'next'
import dynamic from 'next/dynamic'
import { type ParsedUrlQuery } from 'querystring'
import { useContext, useEffect, useRef } from 'react'

import { productPageModuleQuery } from '@data/sanity/queries/page'
import { type SanityProductHeroModule } from '@data/sanity/queries/types/modules'
import { type SanityProductPage } from '@data/sanity/queries/types/page'
import { type SanityProductCatalogueQuery } from '@data/sanity/queries/types/product'
import { type SanitySiteFragment } from '@data/sanity/queries/types/site'
import { getShopifyDomain } from '@lib/shopify/client'
import { getSanityClient } from '@lib/sanity/client'
import {
  defaultModuleQueryParameters,
  getAllProductSlugs,
} from '@lib/sanity/page'
import { getProductCatalogue, getProductPage } from '@lib/sanity/product'
import { AnalyticsEventName } from '@lib/analytics'
import { AnalyticsContext } from '@lib/analytics-context'
import { useViewContentFacebookEvent } from '@lib/facebook'
import { getPriceNumber } from '@lib/format'
import { type Locale, getAllLocales } from '@lib/language'
import { useVariantMetadata } from '@lib/metadata'
import {
  type BasePageProps,
  getNonNullValues,
  getPagePropsFromContext,
  getPageSanityClient,
} from '@lib/page'
import { useDigitalProductContent } from '@lib/product'
import { ProductContext } from '@lib/product-context'
import {
  type ProductReview,
  type ProductReviewSummary,
  type Reviews,
  getProductReviews,
  getProductReviewSummary,
  getReviews,
  ReviewProvider,
} from '@lib/review'
import {
  type CustomStaticPropsContext,
  type StaticPathWithSlug,
} from '@lib/routes'
import { getProductSchema } from '@lib/schema/product'
import { getVideoObjectSchemas } from '@lib/schema/video'
import { ShopContext } from '@lib/shop-context'

import Layout from '@components/layout'
import Modules from '@modules/modules'

const PreviewModules = dynamic(() => import('@modules/preview-modules'))

type ProductPageProps = BasePageProps & {
  slug: string
  page: SanityProductPage
  site: SanitySiteFragment
  productCatalogue: SanityProductCatalogueQuery
  reviews: Reviews
  productReviews: ProductReview[]
  productReviewSummary: ProductReviewSummary
}

interface StaticProductPageParams extends ParsedUrlQuery {
  slug: string
}

/**
 * The product page.
 */
const ProductPage = ({
  draftMode,
  locale,
  slug,
  page,
  site,
  productCatalogue,
  reviews,
  productReviews,
  productReviewSummary,
}: ProductPageProps) => {
  const { currencyCode, taxRate } = useContext(ShopContext)
  const { product, activeVariant } = useContext(ProductContext)
  const { triggerAnalyticsEvent } = useContext(AnalyticsContext)

  const lastProductPageviewPayloadRef = useRef({})

  // Get product schema
  const productSchema = getProductSchema(
    locale,
    productReviews,
    productReviewSummary,
    product,
    activeVariant,
    site.seoSettings?.siteTitle,
    site.generalSettings.siteURL,
    currencyCode,
    taxRate,
  )
  const videoObjectSchemas = getVideoObjectSchemas(page)
  const schemas = [productSchema, ...videoObjectSchemas]

  // Load the correct digital product content
  const [showPrivateContent, isLoaded] = useDigitalProductContent(
    productCatalogue,
    slug,
  )

  // Trigger view content Facebook event
  useViewContentFacebookEvent(
    locale,
    product?.title,
    activeVariant?.variantID,
    activeVariant?.options,
    site.generalSettings?.facebookEvents,
  )

  // Set product variant metadata
  useVariantMetadata(activeVariant)

  // Trigger product pageview event on page load/navigation and active variant change
  useEffect(() => {
    if (!product || !activeVariant || !currencyCode) {
      return
    }

    const payload = {
      currency: currencyCode,
      value: getPriceNumber(activeVariant.price),
      items: [
        {
          index: 0,
          item_id: activeVariant.variantID,
          item_name: product.title,
          item_variant: activeVariant.options
            ?.map((option) => option.value)
            ?.join(', '),
          price: getPriceNumber(activeVariant.price),
          discount: activeVariant.comparePrice
            ? getPriceNumber(activeVariant.comparePrice - activeVariant.price)
            : 0,
          quantity: 1,
        },
      ],
    }

    // Make sure not to send the same payload mulitple times
    const payloadDifference = diff(
      lastProductPageviewPayloadRef.current,
      payload,
    )

    if (!payloadDifference) {
      return
    }

    lastProductPageviewPayloadRef.current = payload
    triggerAnalyticsEvent(AnalyticsEventName.ProductPageview, payload)
  }, [activeVariant, currencyCode, product, triggerAnalyticsEvent])

  return (
    <Layout page={page} site={site} schemas={schemas} draftMode={draftMode}>
      {isLoaded && !draftMode && (
        <Modules
          modules={showPrivateContent ? page.privateModules : page.modules}
          reviews={reviews}
        />
      )}
      {draftMode && (
        <PreviewModules
          modules={page.modules}
          reviews={reviews}
          query={productPageModuleQuery}
          params={{
            ...defaultModuleQueryParameters,
            locale,
            productSlug: slug,
          }}
        />
      )}
    </Layout>
  )
}

export async function getStaticProps(
  context: CustomStaticPropsContext<StaticProductPageParams>,
): Promise<GetStaticPropsResult<ProductPageProps>> {
  const sanityClient = getPageSanityClient(!!context.draftMode)
  const slug = context.params?.slug ?? ''
  const productPage = await getProductPage(sanityClient, context.locale, slug)

  // Get product from product hero module
  const pageModules = productPage.page?.modules ?? []
  const productHeroModule = pageModules.find(
    (pageModule) => pageModule._type === 'productHero',
  ) as SanityProductHeroModule | undefined
  const product = productHeroModule?.product

  if (!productPage.page || !product) {
    return {
      notFound: true,
    }
  }

  // Get basic information for all products
  const productCatalogue = await getProductCatalogue(
    sanityClient,
    context.locale,
  )

  // Get Stamped.io reviews
  const shopifyDomain = getShopifyDomain(context.locale)
  const stampedApiKey = productPage.site.generalSettings.stampedApiKey
  const reviews = await getReviews(
    pageModules,
    shopifyDomain,
    stampedApiKey,
    product.productID,
  )
  const productReviews = await getProductReviews(
    shopifyDomain,
    product.productID,
    {
      provider: ReviewProvider.STAMPED,
      apiKey: stampedApiKey,
    },
  )
  const productReviewSummary = await getProductReviewSummary(
    shopifyDomain,
    product.productID,
    {
      provider: ReviewProvider.STAMPED,
      apiKey: stampedApiKey,
    },
  )

  return {
    props: {
      ...getPagePropsFromContext(context),
      slug,
      page: getNonNullValues(productPage.page),
      site: getNonNullValues(productPage.site),
      productCatalogue,
      reviews,
      productReviews,
      productReviewSummary,
    },
    revalidate: 60,
  }
}

export async function getStaticPaths(): Promise<
  GetStaticPathsResult<StaticProductPageParams>
> {
  if (process.env.NEXT_PUBLIC_APP_ENV === 'preview') {
    return {
      paths: [],
      fallback: 'blocking',
    }
  }

  const sanityClient = getSanityClient()
  const allProducts = await getAllProductSlugs(sanityClient)
  const allLocales = getAllLocales()
  const paths: StaticPathWithSlug[] = allProducts
    .map((product) => ({
      ...product,
      slug: product.slug.current,
      locale: product.locale as Locale,
    }))
    .filter((page) => allLocales.includes(page.locale))
    .filter((page) => !!page.slug)
    .map((page) => ({
      params: {
        slug: page.slug,
      },
      locale: page.locale,
    }))

  return {
    paths,
    fallback: 'blocking',
  }
}

export default ProductPage
